import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MemberFAQSection } from "@/components/documents/faq/MemberFAQSection";
import { DocumentTabNavigation } from "@/components/documents/DocumentTabNavigation";
import { MemberDocumentGridView } from "@/components/documents/MemberDocumentGridView";
import { useLanguage } from "@/contexts/LanguageContext";

interface Section {
  id: string;
  name: string;
  position: number;
  section_type: 'DOCUMENTS' | 'FAQ';
}

interface Document {
  id: string;
  name: string;
  display_name?: string;
  file_path: string;
  section_id: string;
  created_at: string;
  size: number;
  position: number;
  content_type: string;
  created_by: string | null;
  creator_email: string | null;
  creator_name: string | null;
  organization_id: string;
}

const MANDATORY_SECTIONS = ['Stadgar', 'Ordningsregler', 'FAQ'];

const MemberDocumentsPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [sections, setSections] = useState<Section[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const memberId = sessionStorage.getItem('organization_member_id');
        
        if (!memberId) {
          toast.error('Could not determine your organization membership');
          return;
        }
        
        const { data: memberData, error: memberError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('id', memberId)
          .single();
        
        if (memberError || !memberData) {
          toast.error('Could not determine your organization');
          return;
        }
        
        const orgId = memberData.organization_id;
        setOrganizationId(orgId);

        const { data: sectionsData, error: sectionsError } = await supabase
          .from('document_sections')
          .select('*')
          .eq('organization_id', orgId)
          .order('position');

        if (sectionsError) throw sectionsError;
        setSections(sectionsData || []);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load sections');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const loadDocuments = async (sectionId: string) => {
    if (!organizationId) return;
    
    setIsLoadingDocuments(true);
    try {
      const { data: sectionDocs, error: docsError } = await supabase
        .from('documents')
        .select('*')
        .eq('section_id', sectionId)
        .eq('organization_id', organizationId)
        .order('position');

      if (docsError) throw docsError;
      setDocuments(sectionDocs || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleSectionSelect = (section: Section) => {
    setSelectedSection(section);
    if (section.section_type !== 'FAQ') {
      loadDocuments(section.id);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.file_path, 60);
      
      if (error) throw error;
      
      if (data?.signedUrl) {
        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error: any) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  if (isLoading || !organizationId) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pb-20 md:pb-0">
        <Loader2 className="h-8 w-8 animate-spin text-[#2F80ED]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      <div className="max-w-7xl mx-auto px-2 py-4">
        <div className="mb-6">
          <DocumentTabNavigation 
            sections={sections}
            selectedSection={selectedSection}
            onSectionSelect={handleSectionSelect}
            mandatorySections={MANDATORY_SECTIONS}
            isLoading={false}
          />
        </div>
        
          {selectedSection ? (
            selectedSection.section_type === 'FAQ' ? (
              <MemberFAQSection organizationId={organizationId} sectionId={selectedSection.id} />
            ) : (
              <div className="bg-white border border-[#dce0e5] rounded-lg shadow-sm">
                <MemberDocumentGridView
                  selectedSection={selectedSection}
                  documents={documents}
                  onDownload={handleDownload}
                  isLoadingDocuments={isLoadingDocuments}
                />
              </div>
            )
          ) : (
            <div className="bg-white border border-[#dce0e5] rounded-lg shadow-sm">
              <div className="p-8 text-center">
                <p className="text-[#637588]">
                  {t('documents.select_section')}
                </p>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default MemberDocumentsPage;
